/**
 * useMonitoring Hook
 *
 * Hook for fetching monitoring data with auto-refresh support
 * Uses React Query for caching and automatic refetching
 */

import { useQuery } from '@tanstack/react-query';
import { monitoringApi } from '@/lib/api/monitoring';

/**
 * Query keys for monitoring data
 */
export const monitoringQueryKeys = {
  dashboard: ['monitoring', 'dashboard'] as const,
  servicesHealth: ['monitoring', 'services', 'health'] as const,
};

export interface UseMonitoringOptions {
  /**
   * Refresh interval in milliseconds (0 to disable auto-refresh)
   */
  refetchInterval?: number;
  /**
   * Whether to enable auto-refresh
   */
  enabled?: boolean;
}

/**
 * Hook to fetch complete monitoring dashboard data
 */
export function useMonitoringDashboard(options: UseMonitoringOptions = {}) {
  const { refetchInterval = 0, enabled = true } = options;

  return useQuery({
    queryKey: monitoringQueryKeys.dashboard,
    queryFn: monitoringApi.getDashboard,
    refetchInterval,
    enabled,
    staleTime: 0, // Always consider data stale (for real-time monitoring)
    retry: 2,
  });
}

/**
 * Hook to fetch service health status only
 */
export function useServicesHealth(options: UseMonitoringOptions = {}) {
  const { refetchInterval = 0, enabled = true } = options;

  return useQuery({
    queryKey: monitoringQueryKeys.servicesHealth,
    queryFn: monitoringApi.getServicesHealth,
    refetchInterval,
    enabled,
    staleTime: 0,
    retry: 2,
  });
}
